import type { RefObject } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import type { PreferenceCategory, PreferencesBundle } from "./types";
import type { PreferencesApiClient } from "./api";
import {
  updatePreferencesRequest,
  resetPreferencesRequest,
  exportPreferencesRequest,
  importPreferencesRequest,
} from "./api";
import { applyOptimisticUiPreferences } from "./dom-appliers";
import { triggerPreferencesDownload } from "./download";

interface PreferencesMutationsDeps {
  api: PreferencesApiClient;
  organizationId?: string | null;
  refetchPreferences: () => Promise<unknown>;
  downloadRef: RefObject<HTMLAnchorElement | null>;
}

export function usePreferenceMutations({
  api,
  organizationId,
  refetchPreferences,
  downloadRef,
}: PreferencesMutationsDeps) {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (payload: {
      category: PreferenceCategory;
      prefs: Record<string, unknown>;
    }) => updatePreferencesRequest(api, payload),
    onMutate: async ({ category, prefs }) => {
      await queryClient.cancelQueries({
        queryKey: ["user-preferences", organizationId],
      });

      const previousData = queryClient.getQueryData<Partial<PreferencesBundle>>(
        ["user-preferences", organizationId],
      );

      queryClient.setQueryData<Partial<PreferencesBundle>>(
        ["user-preferences", organizationId],
        (old = {}) => ({
          ...old,
          [category]: prefs,
        }),
      );

      if (category === "ui") {
        applyOptimisticUiPreferences(prefs);
      }

      return { previousData };
    },
    onError: (err, _variables, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(
          ["user-preferences", organizationId],
          context.previousData,
        );
      }
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update preferences";
      toast.error(errorMessage);
    },
    onSuccess: () => {
      toast.success("Preferences updated successfully");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["user-preferences", organizationId],
      });
    },
  });

  const resetToDefaults = async (category?: PreferenceCategory) => {
    try {
      await resetPreferencesRequest(api, category);
      await refetchPreferences();
      toast.success(
        category
          ? `${category} preferences reset to defaults`
          : "All preferences reset to defaults",
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to reset preferences";
      toast.error(message);
      throw error;
    }
  };

  const handleExport = async () => {
    try {
      const data = await exportPreferencesRequest(api);
      if (downloadRef.current && typeof window !== "undefined") {
        triggerPreferencesDownload(downloadRef.current, data);
      }
      toast.success("Preferences exported successfully");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to export preferences";
      toast.error(message);
      throw error;
    }
  };

  const importPreferences = async (payload: string) => {
    try {
      await importPreferencesRequest(api, payload);
      await refetchPreferences();
      toast.success("Preferences imported successfully");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to import preferences";
      toast.error(message);
      throw error;
    }
  };

  return {
    updateMutation,
    resetToDefaults,
    handleExport,
    importPreferences,
  };
}
